// Is this URL the QR room-ordering page?
//
// Pure and separate from `RoomOrderApp` so `main.tsx` can ask the question
// without importing the feature's stylesheet — the arrangement every other
// guest surface has, and for the same reason. A false positive puts a guest
// page in front of the front desk; a false negative puts the staff shell in
// front of somebody who scanned a card off a bedside table.
//
// THE MATCH IS ON THE PATH SEGMENT, NOT A PREFIX. `startsWith('/order')` says
// yes to `/orders`, which is exactly the path a kitchen board would take — so a
// prefix match would one day serve a guest page to the kitchen, or hand a
// stranger a route somebody added assuming it was behind a session.

/** True for `/order` and anything under it — `/order/`, `/order/<token>`.
 *
 *  The TOKEN rides in the path because it is what a QR code encodes and what a
 *  browser is given: there is no earlier request to put it in a body. It is
 *  read out of the URL once, on mount, and every call after that sends it in a
 *  POST body instead — see `token.ts` for what that buys and what it does not.
 *
 *  FALSE for `/orders`, which is not ours. */
export function isRoomOrderURL(pathname: string): boolean {
  const clean = pathname.replace(/\/+$/, '')
  return clean === '/order' || clean.startsWith('/order/')
}

/** The token from `/order/<token>`, or '' when the card was scanned badly.
 *
 *  Trimmed and upper-cased, matching the server's `normaliseToken`: a token is
 *  base32, so case is not information, and a trailing slash or a stray space
 *  from a QR reader is a scan artefact rather than an attack.
 *
 *  NOTHING ELSE is stripped. The server compares this in full, and quietly
 *  deleting characters from a secret is how two different tokens start
 *  comparing equal. */
export function tokenFromURL(pathname: string): string {
  const clean = pathname.replace(/\/+$/, '')
  if (!clean.startsWith('/order/')) return ''
  const raw = clean.slice('/order/'.length)
  // Only the first segment. `/order/ABC/anything` is a mis-scan, and taking the
  // whole tail would send a token the server can only answer 404 to — which is
  // right, but "the card is not valid" is a worse message than nothing extra.
  const first = raw.split('/')[0] ?? ''
  return decodeURIComponent(first).trim().toUpperCase()
}
